import { signOut } from "firebase/auth";
import { Eye, LogOut } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext
} from "react-router-dom";

import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { cn } from "@/lib/utils";

import { auth } from "../../../api/firebaseApi";
import { queryClient } from "../../../lib/query";
import { companyPath } from "../../companies/routes";
import { ACCESS_LABEL, initialsOf } from "../../companies/utils";
import { portalTabs, redirectFor } from "../portalModel";
import {
  endPortalPreview,
  portalPreview,
  PREVIEW_PARAM,
  startPortalPreview
} from "../preview";
import { PORTAL_KEY, usePortalMe } from "../queries";
import { PORTAL_SIGN_IN_PATH } from "../routes";
import type { PortalMe } from "../types";

export const usePortal = (): PortalMe => useOutletContext<PortalMe>();

/** Starts a preview from `?preview=<company>:<contact>`; true while one runs. */
const usePreview = (search: string): boolean =>
  useState(() => {
    const param = new URLSearchParams(search).get(PREVIEW_PARAM);
    if (param && param !== portalPreview()) {
      startPortalPreview(param);
      queryClient.removeQueries({ queryKey: PORTAL_KEY });
    }
    return Boolean(portalPreview());
  })[0];

const exitPreview = (companyId: string | undefined): void => {
  endPortalPreview();
  queryClient.removeQueries({ queryKey: PORTAL_KEY });
  window.location.assign(
    companyId ? companyPath(companyId, "contacts") : "/companies"
  );
};

const PreviewBar: FC<{ me: PortalMe }> = ({ me }) => (
  <div className="flex shrink-0 items-center gap-3 border-b border-warn-line bg-warn-soft px-6 py-2 text-base text-warn">
    <Eye className="size-4 shrink-0" />
    <span className="min-w-0 flex-1">
      Previewing {me.contact.name}&apos;s portal (
      {ACCESS_LABEL[me.contact.access]}). Read-only: nothing here is saved or
      sent.
    </span>
    <Button
      size="sm"
      variant="outline"
      className="h-7 bg-background"
      onClick={() => exitPreview(me.company.id)}
    >
      Exit preview
    </Button>
  </div>
);

const useSignedIn = (): boolean | null => {
  const [signedIn, setSignedIn] = useState<boolean | null>(
    auth.currentUser ? true : null
  );
  useEffect(() => auth.onAuthStateChanged((u) => setSignedIn(Boolean(u))), []);
  return signedIn;
};

export const signOutOfPortal = async (): Promise<void> => {
  localStorage.removeItem("loginType");
  localStorage.removeItem("userId");
  queryClient.clear();
  await signOut(auth);
};

const Header: FC<{ me: PortalMe }> = ({ me }) => {
  const navigate = useNavigate();
  return (
    <header className="flex h-14 shrink-0 items-center gap-3.5 border-b border-border bg-background px-6">
      <span className="flex size-7 items-center justify-center rounded-md bg-foreground text-base font-bold text-background">
        {me.firm.name.slice(0, 1).toUpperCase()}
      </span>
      <span className="flex flex-col leading-3.75">
        <span className="text-base font-semibold">{me.firm.name}</span>
        <span className="text-sm text-muted-foreground">
          {me.company.name} portal
        </span>
      </span>
      <nav className="ml-2 flex items-center gap-0.5" aria-label="Portal">
        {portalTabs(me.features).map((t) => (
          <NavLink
            key={t.path}
            to={t.path}
            end
            className={({ isActive }) =>
              cn(
                "inline-flex h-7 items-center rounded-md px-2.5 text-base transition-colors",
                isActive
                  ? "bg-fill-2 text-fg"
                  : "text-fg-2 hover:bg-fill-1 hover:text-fg"
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <span className="flex-1" />
      <span className="flex items-center gap-2">
        <span className="flex size-7 items-center justify-center rounded-md bg-muted text-xs font-semibold">
          {initialsOf(me.contact.name)}
        </span>
        <span className="flex flex-col leading-3.75">
          <span className="text-base font-medium">{me.contact.name}</span>
          {me.contact.title ? (
            <span className="text-sm text-muted-foreground">
              {me.contact.title}
            </span>
          ) : null}
        </span>
      </span>
      {me.preview ? null : (
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label="Sign out"
          onClick={() =>
            void signOutOfPortal().then(() =>
              navigate(PORTAL_SIGN_IN_PATH, { replace: true })
            )
          }
        >
          <LogOut />
        </Button>
      )}
    </header>
  );
};

/** The HR portal frame. Signed-out visitors go to the email link sign-in;
 * a signed-in user who isn't a company contact is told so, not let in. */
export const PortalShell: FC = () => {
  const signedIn = useSignedIn();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const previewing = usePreview(search);
  const me = usePortalMe(signedIn === true);

  if (signedIn === null || (signedIn && me.isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingState size="xl" />
      </div>
    );
  }
  if (!signedIn) return <Navigate to={PORTAL_SIGN_IN_PATH} replace />;
  if (!me.data && previewing) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Empty className="max-w-md border border-border">
          <EmptyHeader>
            <EmptyTitle>This portal can&apos;t be previewed</EmptyTitle>
            <EmptyDescription>
              {me.error?.message ?? "Something went wrong."}
            </EmptyDescription>
          </EmptyHeader>
          <Button variant="outline" onClick={() => exitPreview(undefined)}>
            Exit preview
          </Button>
        </Empty>
      </div>
    );
  }
  if (!me.data) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Empty className="max-w-md border border-border">
          <EmptyHeader>
            <EmptyTitle>
              This account isn&apos;t set up for an HR portal
            </EmptyTitle>
            <EmptyDescription>
              Ask your immigration firm to invite you, or sign in with the email
              they invited.
            </EmptyDescription>
          </EmptyHeader>
          <Button
            variant="outline"
            onClick={() =>
              void signOutOfPortal().then(() =>
                navigate(PORTAL_SIGN_IN_PATH, { replace: true })
              )
            }
          >
            Use a different email
          </Button>
        </Empty>
      </div>
    );
  }
  const redirect = redirectFor(pathname, me.data.features);
  if (redirect) return <Navigate to={redirect} replace />;

  return (
    <div
      className="flex h-screen flex-col bg-fill-1"
      data-testid="portal-shell"
    >
      {me.data.preview ? <PreviewBar me={me.data} /> : null}
      <Header me={me.data} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-280 flex-col gap-5 px-7 py-6">
          <Outlet context={me.data} />
        </div>
      </div>
    </div>
  );
};
